import { HugeiconsIcon } from "@hugeicons/react"
import ArrowRight02Icon from "@hugeicons/core-free-icons/ArrowRight02Icon"
import CheckmarkCircle02Icon from "@hugeicons/core-free-icons/CheckmarkCircle02Icon"
import SparklesIcon from "@hugeicons/core-free-icons/SparklesIcon"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"

type ToolCTAProps = {
  headline: string
  description: string
}

const FEATURES = [
  "Auto-reply to comments with personalized DMs",
  "Keyword-triggered DM automations",
  "AI-powered response generation",
  "Works 24/7 — even while you sleep",
  "1,000 free DMs/month — no credit card",
  "Official Meta partner — zero ban risk",
]

export default function ToolCTA({ headline, description }: ToolCTAProps) {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()

  const handleClick = () => {
    track("cta_clicked", {
      cta_id: "CTA_Tool",
      button_text: "Start Free Trial",
    })
    openAuth()
  }

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 my-16 sm:my-24">
      <div className="relative overflow-hidden rounded-3xl sm:rounded-[2rem] bg-gradient-to-br from-brand-accent via-brand-accent to-brand-accent-hover p-8 sm:p-12 lg:p-14 text-center shadow-[0_20px_60px_-20px_rgba(244,63,94,0.5)]">
        {/* Decorative blobs */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 bg-brand-accent-light/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_50%)] pointer-events-none" />

        <div className="relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs sm:text-sm font-bold uppercase tracking-wider mb-5">
            <HugeiconsIcon icon={SparklesIcon} size={14} color="currentColor" />
            Ready to level up?
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white dark:text-black mb-4 tracking-tight leading-[1.1]">
            {headline}
          </h2>
          <p className="text-white/90 dark:text-black/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
            {description}
          </p>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5 max-w-2xl mx-auto text-left mb-9 sm:mb-11">
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} color="currentColor" className="text-white" />
                </div>
                <span className="text-white/95 dark:text-black/85 text-[15px] font-semibold leading-snug">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA button */}
          <button
            type="button"
            onClick={handleClick}
            className="group inline-flex items-center justify-center gap-2 text-base sm:text-lg font-bold px-10 sm:px-12 py-4 sm:py-4.5 rounded-full bg-white text-brand-accent dark:text-black hover:bg-rose-50 hover:-translate-y-0.5 hover:shadow-[0_15px_40px_-10px_rgba(0,0,0,0.35)] transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.3)]"
          >
            Start Free Trial
            <HugeiconsIcon icon={ArrowRight02Icon} size={20} color="currentColor" className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>

          <p className="text-white/70 dark:text-black/65 text-xs sm:text-sm mt-5 font-medium">
            No credit card required. 1,000 free DMs/month included.
          </p>
        </div>
      </div>
    </section>
  )
}
